/**
 * YoYChart
 *
 * Grouped bar chart comparing Before vs After billing data.
 * When a single professional is selected, renders one full-width chart.
 * When viewing all professionals, facets into a grid of mini-charts
 * with optional go-live date subtitles.
 *
 * Uses recharts BarChart with grouped (non-stacked) bars.
 *
 * The faceted grid mounts a full recharts instance per professional, which at
 * firm scale is a multi-second blocking render that swallows clicks. Two things
 * bound it: only a slice of the grid renders until the reader asks for the rest,
 * and each facet defers mounting until it is near the viewport.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { YoYDataPoint, YoYGranularity } from "../../types/dashboard";
import { YOY_CHART_COLORS, YOY_CHART_LAYOUT } from "../../config/dashboardTheme";
import { formatGoLiveDate } from "../../utils/adminHelpers";

/** Facets rendered before the reader opts into the rest. */
const INITIAL_FACET_COUNT = 20;

/** Title + subtitle block height, so a placeholder reserves the same space as a facet. */
const FACET_HEADER_HEIGHT = 44;

/** Mount a facet before it scrolls into view so the grid never shows a hole. */
const FACET_PRELOAD_MARGIN = "400px";

interface YoYChartProps {
  data: YoYDataPoint[];
  granularity: YoYGranularity;
  selectedProfId: string | null;
  goLiveDates?: Record<string, string>;
}

interface ChartDataPoint {
  label: string;
  Before: number;
  After: number;
}

function buildChartData(
  data: YoYDataPoint[],
  granularity: YoYGranularity
): ChartDataPoint[] {
  // Group by period_num, aggregate hours across both periods
  const map = new Map<number, { Before: number; After: number }>();

  for (const d of data) {
    const existing = map.get(d.period_num) ?? { Before: 0, After: 0 };
    existing[d.period] += d.hours;
    map.set(d.period_num, existing);
  }

  // Sort by period_num and create labels
  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([num, values]) => ({
      label: granularity === "weekly" ? `W${num}` : `M${num}`,
      Before: Math.round(values.Before * 10) / 10,
      After: Math.round(values.After * 10) / 10,
    }));
}

/**
 * Holds a facet's footprint and mounts its chart once it nears the viewport.
 * Observes the viewport rather than a scroll container so it does not depend on
 * which ancestor owns the scrollbar. A mounted facet stays mounted — scrolling
 * back over one should not re-pay its render.
 */
function LazyFacet({
  minHeight,
  children,
}: {
  minHeight: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setMounted(true);
      },
      { rootMargin: FACET_PRELOAD_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);

  return (
    <div ref={ref} style={{ minHeight }}>
      {mounted ? children : null}
    </div>
  );
}

function SingleChart({
  chartData,
  title,
  subtitle,
  height = 350,
}: {
  chartData: ChartDataPoint[];
  title?: string;
  subtitle?: string;
  height?: number;
}) {
  return (
    <div>
      {title && (
        <div className="mb-2">
          <h4 className="text-sm font-medium text-foreground">{title}</h4>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            stroke="var(--muted-foreground)"
          />
          <YAxis tick={{ fontSize: 12 }} stroke="var(--muted-foreground)" />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              fontSize: "13px",
            }}
            formatter={(value) => [`${Number(value)} hrs`, undefined]}
          />
          <Legend />
          {/* Mount animation costs a frame budget per facet, which the grid
              cannot afford once there are many of them. */}
          <Bar
            dataKey="Before"
            fill={YOY_CHART_COLORS.before}
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
            isAnimationActive={false}
          />
          <Bar
            dataKey="After"
            fill={YOY_CHART_COLORS.after}
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function YoYChart({
  data,
  granularity,
  selectedProfId,
  goLiveDates,
}: YoYChartProps) {
  // All professionals: faceted grid sorted by go-live date (earliest first)
  // NOTE: hooks must be called before any conditional return (Rules of Hooks)
  const [showAllFacets, setShowAllFacets] = useState(false);

  const profGroups = useMemo(() => {
    // Key by prof_id (not prof_name) to avoid merging data for attorneys
    // who share the same full name.
    const groups = new Map<string, { profName: string; points: YoYDataPoint[] }>();
    for (const d of data) {
      const existing = groups.get(d.prof_id) ?? { profName: d.prof_name, points: [] };
      existing.points.push(d);
      groups.set(d.prof_id, existing);
    }
    // Sort by Hourglass go-live date ascending — earliest adopters first.
    // Professionals without a go-live date sort to the end.
    return Array.from(groups.entries()).sort(([idA], [idB]) => {
      const dateA = goLiveDates?.[idA] ?? "9999-12-31";
      const dateB = goLiveDates?.[idB] ?? "9999-12-31";
      return dateA.localeCompare(dateB);
    });
  }, [data, goLiveDates]);

  // Single professional: one full chart
  if (selectedProfId) {
    const chartData = buildChartData(data, granularity);
    return <SingleChart chartData={chartData} height={YOY_CHART_LAYOUT.singleChartHeight} />;
  }

  if (profGroups.length === 0) {
    return (
      <p className="text-center text-muted-foreground text-sm py-8">
        No chart data available for the selected period.
      </p>
    );
  }

  const visibleGroups = showAllFacets
    ? profGroups
    : profGroups.slice(0, INITIAL_FACET_COUNT);
  const hiddenCount = profGroups.length - visibleGroups.length;
  const facetMinHeight = YOY_CHART_LAYOUT.gridChartHeight + FACET_HEADER_HEIGHT;

  return (
    <>
      <div className={YOY_CHART_LAYOUT.gridClass}>
        {visibleGroups.map(([profId, { profName, points }]) => {
          const glDate = goLiveDates?.[profId];
          const subtitle = glDate
            ? `Go-live: ${formatGoLiveDate(glDate)}`
            : undefined;

          return (
            <LazyFacet key={profId} minHeight={facetMinHeight}>
              <SingleChart
                title={profName}
                subtitle={subtitle}
                chartData={buildChartData(points, granularity)}
                height={YOY_CHART_LAYOUT.gridChartHeight}
              />
            </LazyFacet>
          );
        })}
      </div>

      {hiddenCount > 0 && (
        <div className="flex justify-center pt-6">
          <button
            onClick={() => setShowAllFacets(true)}
            className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Show {hiddenCount} more{" "}
            {hiddenCount === 1 ? "timekeeper" : "timekeepers"}
          </button>
        </div>
      )}
    </>
  );
}
